<template>
    <div class="common-image-panel">
        <div class="common-image-panel_container">
            <div
                class="common-image-panel_content"
                draggable="false"
                @mousedown.stop="service.startMove"
                @mousemove.stop="service.moveImage"
                @mouseup.stop="service.endMove"
            >
                <img :src="currentImage.storage_path" :style="imageStyle" alt="" class="absolute h-full w-full select-none object-contain" draggable="false" loading="lazy">
                <div class="common-image-panel_control">
                    <button class="th-btn-only-icon th-btn th-btn-plain mb-1" @click.stop="service.zoomIn">
                        <IconAddMedium class="th-btn-icon h-6 w-6"/>
                    </button>
                    <button class="th-btn-only-icon th-btn th-btn-plain mb-1" @click.stop="service.zoomOut">
                        <IconMinus class="th-btn-icon h-6 w-6"/>
                    </button>
                    <button class="th-btn-only-icon th-btn th-btn-plain"  @click.stop="service.rotatePicture">
                        <IconRotate class="th-btn-icon h-6 w-6"/>
                    </button>
                </div>
            </div>
            <div class="common-image-panel_info">{{service.currentImageIndex + 1}}/{{service.imageList.length}} images </div>
        </div>
        <div class="common-image-panel_banner">
            <div :style="bannerStyle" class="common-image-panel_content">
                <div
                    v-for="(img, idx) in service.imageList"
                    :key="img.image_id"
                    class="common-image-panel_image-box"
                    :class="{'common-image-panel_image-active': service.currentImageIndex === idx}"
                    @click.stop="service.selectImage(idx)"
                >
                    <img
                        :src="img.storage_path"
                        alt=""
                        class="absolute left-1/2 top-1/2 max-h-full max-w-full -translate-x-1/2 -translate-y-1/2"
                        loading="lazy"
                    >
                </div>
            </div>
        </div>
        <div
            v-show="service.currentImageIndex > 0"
            class="common-image-panel_left"
            @click.stop="service.toPrev"
        >
            <IconArrowLeft class="fill-[#616161]"/>
        </div>
        <div
            v-show="service.currentImageIndex < service.imageList.length - 1"
            class="common-image-panel_right"
            @click.stop="service.toNext"
        >
            <IconArrowRight class="fill-[#616161]"/>
        </div>
        <div class="common-image-panel_close" @click.stop="service.close()">
            <IconClose class="h-5 w-5 fill-[#616161]"/>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { ImagePanelService } from './services/image-panel-service';
import IconAddMedium from './icons/IconAddMedium.vue';
import IconMinus from './icons/IconMinus.vue';
import IconRotate from './icons/IconRotate.vue';
import IconClose from './icons/IconClose.vue';
import IconArrowLeft from './icons/IconArrowLeft.vue';
import IconArrowRight from './icons/IconArrowRight.vue';

const props = defineProps<{
    service: ImagePanelService,
}>();

const bannerStyle = computed(() => {
    let left = 0;
    const winWidth = document.body.clientWidth;
    const width = props.service.imageList.length * 80;
    const maxNum = parseInt(`${winWidth / 80}`, 10);
    if (width > winWidth) {
        if (props.service.currentImageIndex + 1 > maxNum) {
            const page = parseInt(`${(props.service.currentImageIndex) / maxNum}`, 10);
            left = page * maxNum * 80;
            const maxLeft = width - winWidth;
            left = -(left > maxLeft ? maxLeft : left);
        } else {
            left = 0;
        }
    } else {
        left = (winWidth - width) / 2;
    }
    return {
        left: `${left}px`,
        width: `${width}px`,
    };
})

const currentImage = computed(() => {
    return props.service.imageList[props.service.currentImageIndex] || {}
});

const imageStyle = computed(() => {
    return {
        left: `calc( 50%  + ${props.service.position.left}px )`,
        top: `calc( 50%  + ${props.service.position.top}px )`,
        transform: `translate(-50%,-50%) rotate(${(props.service.rateNum % 4) * 90}deg) scale(${props.service.size},${props.service.size})`,
    };
})

</script>

<style lang="scss">
.common-image-panel {
    @apply fixed top-0 left-0 h-full w-full;
    z-index: 2000;
    background: rgba(0,0,0,.7);
    .common-image-panel_container {
        @apply absolute rounded bg-white;
        top: 5vh;
        left: 96px;
        height: calc(100% - 5vh - 118px);
        width: calc(100% - 192px);
        z-index: 2001;
    }
    .common-image-panel_content {
        @apply relative overflow-hidden w-full h-full text-center cursor-all-scroll;
    }
    .common-image-panel_control {
        @apply absolute bg-white rounded p-1 top-1/2 transform -translate-y-1/2;
        z-index: 4;
        right: 0.2em;
        width: min-content;
        border: 1px solid #dfe1e6;
        box-shadow: 0 0 4px 0 rgba(0,0,0,.08), 0 2px 6px 0 rgba(0,0,0,.08);
    }
    .common-image-panel_image-box {
        @apply inline-block rounded mr-2.5 bg-white overflow-hidden border border-white text-center cursor-pointer relative;
        width: 70px;
        height: 70px;
        &.common-image-panel_image-active {
            @apply relative;
            border-color: #4a90e2;
            &:after {
                @apply block absolute top-0 left-0 w-full h-full;
                content: '';
                background: rgba(74,144,226,.4);
            }
        }
    }
    .common-image-panel_info {
        @apply absolute text-center w-full text-white text-xs leading-3;
        bottom: calc(-8px - 1em);
    }
    .common-image-panel_banner {
        @apply absolute bottom-5 w-full overflow-hidden;
        height: 72px;
    }
    .common-image-panel_left, .common-image-panel_right {
        @apply absolute rounded cursor-pointer flex items-center justify-center;
        width: 48px;
        height: 120px;
        background: hsla(0,0%,100%,.7);
        text-align: center;
        line-height: 120px;
        top: calc(50% - 50px);
        margin-top: -60px;
        color: #616161;
    }
    .common-image-panel_left {
        left: 32px;
    }
    .common-image-panel_right {
        right: 32px;
    }
    .common-image-panel_close {
        @apply absolute bg-white flex justify-center items-center;
        color: #616161;
        width: 48px;
        height: 48px;
        text-align: center;
        top: 20px;
        right: 16px;
        border-radius: 100%;
        font-size: 24px;
        cursor: pointer;
        line-height: 50px;
        user-select: none;
    }
}
</style>
